// src/pages/Clinic/ClinicFinanzasPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Typography, CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, Stack, Dialog, DialogContent, DialogActions, ToggleButtonGroup,
  ToggleButton, InputAdornment, IconButton, Autocomplete, Card, CardContent,
  TableContainer, ListSubheader
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon, Download as DownloadIcon, TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon, AttachMoney as MoneyIcon, Close as CloseIcon,
  Category as CategoryIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon
} from '@mui/icons-material';
import axiosInstance from '../../axiosInstance';
import { toast } from 'react-toastify';
import { useClinic } from '../../context/ClinicContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title, Tooltip, Legend, Filler
);

// Catálogo de monedas (igual que en FinanzasPage.jsx)
const currencies = [
  { code: 'USD', symbol: '$', name: 'Dólar Estadounidense' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'HNL', symbol: 'L', name: 'Lempira Hondureño' },
  { code: 'MXN', symbol: '$', name: 'Peso Mexicano' },
  { code: 'GBP', symbol: '£', name: 'Libra Esterlina' },
  { code: 'COP', symbol: '$', name: 'Peso Colombiano' },
  { code: 'PEN', symbol: 'S/.', name: 'Sol Peruano' },
  { code: 'ARS', symbol: '$', name: 'Peso Argentino' },
];

const categoriasGastos = [
  'Renta del consultorio', 'Implementos médicos', 'Material de oficina',
  'Servicios (luz, agua, internet)', 'Mantenimiento', 'Otros gastos'
];
const categoriasIngresos = [
  'Consulta', 'Procedimientos', 'Emergencias', 'Seguros médicos', 'Otros ingresos'
];

const fmtMoney = (n) =>
  new Intl.NumberFormat('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

const monthKey = (isoDateStr) => {
  try { const d = new Date(isoDateStr); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}`; }
  catch { return 'N/A'; }
};

// Paginación robusta (DRF o arreglo plano)
async function fetchAllTransacciones(config) {
  const results = [];
  let url = '/transacciones/';
  while (url) {
    const { data } = await axiosInstance.get(url, config);
    if (Array.isArray(data)) return data;
    results.push(...(data.results || []));
    url = data.next ? data.next.replace(/^https?:\/\/[^/]+\/api\//, '/') : null;
  }
  return results;
}

const ClinicFinanzasPage = () => {
  const theme = useTheme();
  const { selectedClinicId, activeClinic } = useClinic();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // Filtros (con categorías dependientes del tipo)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tipo, setTipo] = useState('');     // '', 'ingreso', 'gasto'
  const [categoria, setCategoria] = useState('');
  const [search, setSearch] = useState('');
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Moneda visual (encabezado)
  const [selectedCurrency, setSelectedCurrency] = useState(() => localStorage.getItem('preferredCurrency') || 'HNL');
  const [currencySymbol, setCurrencySymbol] = useState(() => localStorage.getItem('currencySymbol') || 'L');

  // Diálogo: Nueva transacción
  const [openDialog, setOpenDialog] = useState(false);
  const [tipoTx, setTipoTx] = useState('ingreso');
  const [catTx, setCatTx] = useState('');
  const [descTx, setDescTx] = useState('');
  const [montoTx, setMontoTx] = useState('');
  const [fechaTx, setFechaTx] = useState(new Date().toISOString().split('T')[0]);

  // Diálogo: Editar transacción
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [tipoEdit, setTipoEdit] = useState('ingreso');
  const [catEdit, setCatEdit] = useState('');
  const [descEdit, setDescEdit] = useState('');
  const [montoEdit, setMontoEdit] = useState('');
  const [fechaEdit, setFechaEdit] = useState(new Date().toISOString().split('T')[0]);

  const loadData = async (signal) => {
    if (!selectedClinicId) return;
    setLoading(true);
    try {
      const params = { clinic_id: selectedClinicId, page_size: 200 };
      if (startDate) params.start_date = startDate.toISOString().slice(0,10);
      if (endDate) params.end_date = endDate.toISOString().slice(0,10);
      if (tipo) params.tipo = tipo;
      if (categoria) params.categoria = categoria;
      if (search && search.trim().length >= 2) params.search = search.trim();

      const config = { signal, headers: { 'X-Clinic-Id': String(selectedClinicId) }, params };
      const all = await fetchAllTransacciones(config);
      setRows(all);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error(e);
      toast.error('No se pudieron cargar las transacciones de la clínica.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    loadData(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicId]);

  // Persistir moneda preferida (solo símbolo)
  useEffect(() => {
    localStorage.setItem('preferredCurrency', selectedCurrency);
    const currency = currencies.find(c => c.code === selectedCurrency);
    if (currency) {
      setCurrencySymbol(currency.symbol);
      localStorage.setItem('currencySymbol', currency.symbol);
    }
  }, [selectedCurrency]);

  const aplicarFiltros = async () => {
    const ctrl = new AbortController();
    setLoadingFilters(true);
    await loadData(ctrl.signal);
    setLoadingFilters(false);
  };

  // Agregados
  const { ingresos, gastos, balance, byCategory, monthly, ingresosPorDoctorTop } = useMemo(() => {
    let ingresos = 0, gastos = 0;
    const byCategory = {}; // solo ingresos
    const monthly = {};    // { 'YYYY-MM': { ingresos, gastos } }
    const byDoctor = {};   // ingresos por doctor

    for (const t of rows) {
      const tp = (t.tipo || '').toLowerCase();
      const monto = Number(t.monto) || 0;
      const cat = t.categoria || 'Otros';
      const mk = monthKey(t.fecha);
      const doctorName = t.medico_nombre || `Médico #${t.medico || t.medico_id || ''}`;

      monthly[mk] = monthly[mk] || { ingresos: 0, gastos: 0 };

      if (tp === 'ingreso') {
        ingresos += monto;
        monthly[mk].ingresos += monto;
        byCategory[cat] = (byCategory[cat] || 0) + monto;
        byDoctor[doctorName] = (byDoctor[doctorName] || 0) + monto;
      } else if (tp === 'gasto') {
        gastos += monto;
        monthly[mk].gastos += monto;
      }
    }
    const balance = ingresos - gastos;
    const ingresosPorDoctorTop = Object.entries(byDoctor)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([nombre, total]) => ({ nombre, total }));

    return { ingresos, gastos, balance, byCategory, monthly, ingresosPorDoctorTop };
  }, [rows]);

  const monthLabels = useMemo(() => {
    const keys = Object.keys(monthly).sort();
    return keys.slice(-12);
  }, [monthly]);

  const doughnutData = useMemo(() => {
    const labels = Object.keys(byCategory);
    const data = labels.map(l => byCategory[l]);
    return {
      labels,
      datasets: [{
        label: 'Ingresos',
        data,
        backgroundColor: labels.map((_, i) => alpha(theme.palette.primary.main, 0.25 + (0.5 * (i % 6) / 6))),
        borderColor: labels.map(() => theme.palette.primary.main),
        borderWidth: 1
      }]
    };
  }, [byCategory, theme]);

  const monthlyBarData = useMemo(() => {
    const inc = monthLabels.map(m => monthly[m]?.ingresos || 0);
    const exp = monthLabels.map(m => monthly[m]?.gastos || 0);
    return {
      labels: monthLabels,
      datasets: [
        {
          label: 'Ingresos',
          data: inc,
          backgroundColor: alpha(theme.palette.success.main, 0.7),
          borderColor: theme.palette.success.main,
          borderWidth: 1,
          borderRadius: 4
        },
        {
          label: 'Gastos',
          data: exp,
          backgroundColor: alpha(theme.palette.error.main, 0.7),
          borderColor: theme.palette.error.main,
          borderWidth: 1,
          borderRadius: 4
        }
      ]
    };
  }, [monthLabels, monthly, theme]);

  const doctorBarData = useMemo(() => {
    const labels = ingresosPorDoctorTop.map(d => d.nombre);
    const data = ingresosPorDoctorTop.map(d => d.total);
    return {
      labels,
      datasets: [
        {
          label: 'Ingresos',
          data,
          backgroundColor: alpha(theme.palette.info.main, 0.7),
          borderColor: theme.palette.info.main,
          borderWidth: 1,
          borderRadius: 6,
        }
      ]
    };
  }, [ingresosPorDoctorTop, theme]);

  const exportCSV = () => {
    const header = ['Fecha', 'Tipo', 'Categoría', 'Descripción', 'Monto', 'Médico'];
    const lines = rows.map(r => ([
      r.fecha || '',
      (r.tipo || '').toUpperCase(),
      r.categoria || '',
      (r.descripcion || '').replace(/\n/g, ' ').trim(),
      Number(r.monto || 0).toFixed(2),
      r.medico_nombre || `Médico #${r.medico || r.medico_id || ''}`
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finanzas_clinica_${(activeClinic?.clinica?.nombre || 'clinica').replace(/\s+/g,'_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      const clinicName = activeClinic?.clinica?.nombre || 'Clínica';
      doc.setFontSize(18);
      doc.text(`Reporte Financiero - ${clinicName}`, 105, 18, { align: 'center' });

      // Período
      doc.setFontSize(12);
      let periodo = 'Período: Completo';
      if (startDate || endDate) {
        const sd = startDate ? startDate.toISOString().slice(0,10) : 'inicio';
        const ed = endDate ? endDate.toISOString().slice(0,10) : 'hoy';
        periodo = `Período: ${sd} a ${ed}`;
      }
      doc.text(periodo, 20, 30);

      // Resumen
      doc.setFontSize(14);
      doc.text('Resumen', 20, 42);
      doc.setFontSize(12);
      doc.text(`Ingresos totales: ${currencySymbol}${fmtMoney(ingresos)}`, 30, 52);
      doc.text(`Gastos totales:   ${currencySymbol}${fmtMoney(gastos)}`, 30, 60);
      doc.text(`Balance:          ${currencySymbol}${fmtMoney(balance)}`, 30, 68);

      // Tabla (sin columna Moneda)
      const tableColumn = ['Fecha', 'Tipo', 'Categoría', 'Descripción', 'Monto', 'Médico'];
      const tableRows = rows.map(r => ([
        r.fecha || '',
        (r.tipo || '').toUpperCase(),
        r.categoria || '',
        (r.descripcion || '').replace(/\n/g, ' ').trim(),
        Number(r.monto || 0).toFixed(2),
        r.medico_nombre || `#${r.medico || r.medico_id || ''}`
      ]));

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 78,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [29, 78, 137], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 18 },
          2: { cellWidth: 28 },
          3: { cellWidth: 60 },
          4: { cellWidth: 22, halign: 'right' },
          5: { cellWidth: 32 },
        },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Generado con MedifyHC • Página ${i} de ${pageCount}`, 105, doc.internal.pageSize.height - 10, { align: 'center' });
      }

      const periodTag = (startDate || endDate)
        ? `${startDate ? startDate.toISOString().slice(0,10) : 'inicio'}_${endDate ? endDate.toISOString().slice(0,10) : 'hoy'}`
        : 'Completo';
      doc.save(`Reporte_Financiero_Clinica_${periodTag}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo generar el PDF.');
    }
  };

  // Crear transacción
  const handleCreateTx = async () => {
    try {
      if (!tipoTx || !catTx || !montoTx || !fechaTx) {
        toast.info('Completa tipo, categoría, monto y fecha.');
        return;
      }
      const payload = {
        tipo: tipoTx,
        categoria: catTx,
        descripcion: (descTx || '').trim(),
        monto: parseFloat(montoTx),
        fecha: fechaTx,
        moneda: (activeClinic?.clinica?.moneda || 'USD').toUpperCase(),
      };
      await axiosInstance.post('/transacciones/', payload, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) },
      });
      toast.success('Transacción creada.');
      setOpenDialog(false);
      setTipoTx('ingreso'); setCatTx(''); setDescTx(''); setMontoTx(''); setFechaTx(new Date().toISOString().split('T')[0]);
      const ctrl = new AbortController();
      await loadData(ctrl.signal);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo crear la transacción.');
    }
  };

  // Abrir edición
  const handleOpenEdit = (tx) => {
    setEditingTx(tx);
    setTipoEdit(tx.tipo || 'ingreso');
    setCatEdit(tx.categoria || '');
    setDescEdit(tx.descripcion || '');
    setMontoEdit(String(tx.monto ?? ''));
    setFechaEdit(tx.fecha || new Date().toISOString().split('T')[0]);
    setOpenEditDialog(true);
  };

  // Guardar edición
  const handleSaveEdit = async () => {
    try {
      if (!editingTx) return;
      if (!tipoEdit || !catEdit || !montoEdit || !fechaEdit) {
        toast.info('Completa tipo, categoría, monto y fecha.');
        return;
      }
      const payload = {
        tipo: tipoEdit,
        categoria: catEdit,
        descripcion: (descEdit || '').trim(),
        monto: parseFloat(montoEdit),
        fecha: fechaEdit,
        moneda: (activeClinic?.clinica?.moneda || 'USD').toUpperCase(),
      };
      await axiosInstance.put(`/transacciones/${editingTx.id}/`, payload, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) },
      });
      toast.success('Transacción actualizada.');
      setOpenEditDialog(false);
      setEditingTx(null);
      const ctrl = new AbortController();
      await loadData(ctrl.signal);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar la transacción.');
    }
  };

  // Eliminar
  const handleDelete = async (txId) => {
    if (!window.confirm('¿Eliminar esta transacción?')) return;
    try {
      await axiosInstance.delete(`/transacciones/${txId}/`, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) },
      });
      toast.success('Transacción eliminada.');
      const ctrl = new AbortController();
      await loadData(ctrl.signal);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo eliminar la transacción.');
    }
  };

  if (!selectedClinicId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Selecciona una clínica para ver Finanzas</Typography>
      </Box>
    );
  }

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
  }

  // Tarjetas (idéntica forma a FinanzasPage.jsx)
  const MetricCard = ({ icon, title, value, color }) => (
    <Card sx={{ height: '100%', boxShadow: 3, borderRadius: 4 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" component="h2" sx={{ ml: 1 }}>{title}</Typography>
        </Box>
        <Typography variant="h4" component="p" sx={{ fontWeight: 'bold', color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  const ingresosFmt = `${currencySymbol}${fmtMoney(ingresos)}`;
  const gastosFmt = `${currencySymbol}${fmtMoney(gastos)}`;
  const balanceFmt = `${currencySymbol}${fmtMoney(balance)}`;

  // Categorías en filtros:
  // - Si hay tipo => lista específica
  // - Si no hay tipo => unión de ambas con separadores
  const categoriasFiltro = tipo === 'ingreso'
    ? categoriasIngresos
    : tipo === 'gasto'
      ? categoriasGastos
      : []; // solo usado si tipo tiene valor
  const showGroupedCategories = tipo === '';

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 }, py: 4 }}>
      {/* Encabezado coherente con FinanzasPage.jsx */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MoneyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
            Finanzas de la Clínica
          </Typography>
        </Box>
        {/* Selector de moneda (encabezado, no filtro) */}
        <FormControl sx={{ minWidth: 250 }}>
          <Autocomplete
            options={currencies}
            getOptionLabel={(option) => `${option.symbol} - ${option.name} (${option.code})`}
            value={currencies.find(c => c.code === selectedCurrency) || null}
            onChange={(event, newValue) => { if (newValue) setSelectedCurrency(newValue.code); }}
            disableClearable
            renderInput={(params) => (<TextField {...params} label="Moneda" size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}/>)}
          />
        </FormControl>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={theme.spacing(3)} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Ingresos Totales"
            value={ingresosFmt}
            color={theme.palette.success.main}
            icon={<TrendingUpIcon sx={{ fontSize: 32, color: 'success.main' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Gastos Totales"
            value={gastosFmt}
            color={theme.palette.error.main}
            icon={<TrendingDownIcon sx={{ fontSize: 32, color: 'error.main' }} />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Balance"
            value={balanceFmt}
            color={balance >= 0 ? theme.palette.success.main : theme.palette.error.main}
            icon={<MoneyIcon sx={{ fontSize: 32, color: balance >= 0 ? 'success.main' : 'error.main' }} />}
          />
        </Grid>
      </Grid>

      {/* Barra de acciones + filtros */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} sx={{ borderRadius: '20px', px: 3 }}>
          Nueva Transacción
        </Button>

        {/* Filtro por tipo */}
        <ToggleButtonGroup
          color="primary"
          value={tipo}
          exclusive
          onChange={(event, newFilter) => { if (newFilter !== null) { setTipo(newFilter); setCategoria(''); } }}
          aria-label="filtro tipo de transacción"
          size="small"
          sx={{ '& .MuiToggleButton-root': { borderRadius: '16px', mx: 0.5 } }}
        >
          <ToggleButton value="">Todos</ToggleButton>
          <ToggleButton value="ingreso">Ingresos</ToggleButton>
          <ToggleButton value="gasto">Gastos</ToggleButton>
        </ToggleButtonGroup>

        {/* Rango de fechas */}
        <DatePicker label="Desde" value={startDate} onChange={(v) => setStartDate(v)} slotProps={{ textField: { size: 'small' } }} />
        <DatePicker label="Hasta" value={endDate} onChange={(v) => setEndDate(v)} slotProps={{ textField: { size: 'small' } }} />

        {/* Categoría:
            - Si tipo === '' => mostrar grupos con subheaders
            - Si hay tipo   => lista específica */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Categoría</InputLabel>
          <Select
            label="Categoría"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            sx={{ borderRadius: '12px' }}
          >
            <MenuItem value="">Todas</MenuItem>
            {showGroupedCategories ? [
              <ListSubheader key="header-ingresos" disableSticky>Ingresos</ListSubheader>,
              ...categoriasIngresos.map((c) => (
                <MenuItem key={`ing-${c}`} value={c}>{c}</MenuItem>
              )),
              <ListSubheader key="header-gastos" disableSticky>Gastos</ListSubheader>,
              ...categoriasGastos.map((c) => (
                <MenuItem key={`gas-${c}`} value={c}>{c}</MenuItem>
              ))
            ] : (
              categoriasFiltro.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Buscar"
          placeholder="Descripción/Categoría"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <Button variant="contained" onClick={aplicarFiltros} disabled={loadingFilters} sx={{ borderRadius: '20px', px: 3 }}>
          {loadingFilters ? 'Aplicando...' : 'Aplicar filtros'}
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV} sx={{ borderRadius: '20px', px: 3 }}>
          Exportar CSV
        </Button>
        <Button variant="tonal" startIcon={<DownloadIcon />} onClick={exportPDF} sx={{ borderRadius: '20px', px: 3 }}>
          Descargar PDF
        </Button>
      </Box>

      {/* Gráficos */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', boxShadow: theme.shadows[2], height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Distribución de ingresos por categoría
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 220, sm: 300 } }}>
              {Object.keys(byCategory).length ? (
                <Doughnut
                  data={doughnutData}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } } }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No hay datos suficientes</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', boxShadow: theme.shadows[2], height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Ingresos vs Gastos por mes
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 240, sm: 320 } }}>
              {monthLabels.length ? (
                <Bar
                  data={monthlyBarData}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true } } }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No hay datos suficientes</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabla (forma igual a FinanzasPage.jsx) */}
      <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 2, borderRadius: 4 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', fontSize: '1.75rem', color: theme.palette.primary.main }}>
            Historial de Transacciones
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
          <Table stickyHeader size="medium" sx={{
            '& .MuiTableCell-root': { fontSize: '1.07rem' },
            '& .MuiTableCell-head': { fontSize: '1.2rem', fontWeight: 'bold' }
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: theme.palette.background.default }}>Fecha</TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.background.default }}>Tipo</TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.background.default }}>Categoría</TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.background.default }}>Descripción</TableCell>
                <TableCell align="right" sx={{ backgroundColor: theme.palette.background.default }}>Monto</TableCell>
                <TableCell sx={{ backgroundColor: theme.palette.background.default }}>Médico</TableCell>
                <TableCell align="right" sx={{ backgroundColor: theme.palette.background.default }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '1.1rem' }}>
                    No hay transacciones que coincidan con los filtros seleccionados.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{new Date(r.fecha).toLocaleDateString('es-ES')}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          px: 1.5, py: 0.5, borderRadius: '16px',
                          fontSize: '0.75rem',
                          color: r.tipo === 'ingreso' ? theme.palette.success.contrastText : theme.palette.error.contrastText,
                          bgcolor: r.tipo === 'ingreso' ? theme.palette.success.main : theme.palette.error.main
                        }}
                      >
                        {r.tipo === 'ingreso' ? 'Ingreso' : 'Gasto'}
                      </Box>
                    </TableCell>
                    <TableCell>{r.categoria}</TableCell>
                    <TableCell>{r.descripcion}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'medium', color: r.tipo === 'ingreso' ? 'success.main' : 'error.main' }}>
                      {currencySymbol}{fmtMoney(parseFloat(r.monto))}
                    </TableCell>
                    <TableCell>{r.medico_nombre || `Médico #${r.medico || r.medico_id || ''}`}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={() => handleOpenEdit(r)} sx={{ mr: 1 }}>
                          <EditIcon fontSize="small" color="primary" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(r.id)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Diálogo: Nueva Transacción (Material 3) */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { width: '95%', maxWidth: '800px', height: 'auto', maxHeight: '90vh', borderRadius: '28px', overflow: 'hidden' }
        }}
      >
        <Box sx={{
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.dark, 0.2) : alpha(theme.palette.primary.light, 0.1),
          p: 3, position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.1),
              color: tipoTx === 'ingreso' ? theme.palette.success.main : theme.palette.error.main
            }}>
              {tipoTx === 'ingreso' ? <TrendingUpIcon fontSize="medium" /> : <TrendingDownIcon fontSize="medium" />}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">{tipoTx === 'ingreso' ? 'Nuevo Ingreso' : 'Nuevo Gasto'}</Typography>
              <Typography variant="body2" color="text.secondary">Complete los detalles de la transacción</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpenDialog(false)} sx={{
            position: 'absolute', right: 16, top: 16,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
            '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.8) },
          }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Tipo */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tipo de Transacción</Typography>
            <ToggleButtonGroup
              value={tipoTx} exclusive fullWidth
              onChange={(e, v) => { if (v) { setTipoTx(v); setCatTx(''); } }}
              aria-label="tipo de transacción"
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: 0, borderRadius: '16px !important', mx: 0.5, py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: tipoTx === 'ingreso' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                    color: tipoTx === 'ingreso' ? theme.palette.success.main : theme.palette.error.main,
                    '&:hover': {
                      bgcolor: tipoTx === 'ingreso' ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2),
                    }
                  }
                }
              }}
            >
              <ToggleButton value="ingreso" sx={{ borderRadius: '16px', flexDirection: 'column', gap: 1, height: '80px' }}>
                <TrendingUpIcon color={tipoTx === 'ingreso' ? 'success' : 'inherit'} />
                <Typography variant="body2">Ingreso</Typography>
              </ToggleButton>
              <ToggleButton value="gasto" sx={{ borderRadius: '16px', flexDirection: 'column', gap: 1, height: '80px' }}>
                <TrendingDownIcon color={tipoTx === 'gasto' ? 'error' : 'inherit'} />
                <Typography variant="body2">Gasto</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="cat-tx-label">Categoría</InputLabel>
                <Select
                  labelId="cat-tx-label" label="Categoría" value={catTx} onChange={(e) => setCatTx(e.target.value)}
                  sx={{
                    borderRadius: '16px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: tipoTx === 'ingreso' ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.error.main, 0.3),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: tipoTx === 'ingreso' ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.error.main, 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: tipoTx === 'ingreso' ? theme.palette.success.main : theme.palette.error.main,
                    }
                  }}
                  startAdornment={<InputAdornment position="start"><CategoryIcon color="action" /></InputAdornment>}
                >
                  {(tipoTx === 'ingreso' ? categoriasIngresos : categoriasGastos).map(cat => (
                    <MenuItem key={cat} value={cat}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tipoTx === 'ingreso' ? theme.palette.success.main : theme.palette.error.main }} />
                        {cat}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth label="Descripción" value={descTx} onChange={(e) => setDescTx(e.target.value)}
                multiline rows={3} placeholder="Añade detalles sobre esta transacción..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Monto" value={montoTx} onChange={(e) => setMontoTx(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="Fecha" value={fechaTx} onChange={(e) => setFechaTx(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 3, pt: 2 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ borderRadius: '20px', px: 3, py: 1 }}>
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleCreateTx}
            sx={{
              borderRadius: '20px', px: 3, py: 1,
              bgcolor: tipoTx === 'ingreso' ? theme.palette.success.main : theme.palette.error.main,
              '&:hover': { bgcolor: tipoTx === 'ingreso' ? theme.palette.success.dark : theme.palette.error.dark },
            }}
            startIcon={tipoTx === 'ingreso' ? <TrendingUpIcon /> : <TrendingDownIcon />}
          >
            {tipoTx === 'ingreso' ? 'Registrar Ingreso' : 'Registrar Gasto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Editar Transacción */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { width: '95%', maxWidth: '800px', height: 'auto', maxHeight: '90vh', borderRadius: '28px', overflow: 'hidden' }
        }}
      >
        <Box sx={{
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.dark, 0.2) : alpha(theme.palette.primary.light, 0.1),
          p: 3, position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.1),
              color: tipoEdit === 'ingreso' ? theme.palette.success.main : theme.palette.error.main
            }}>
              {tipoEdit === 'ingreso' ? <TrendingUpIcon fontSize="medium" /> : <TrendingDownIcon fontSize="medium" />}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold" color={tipoEdit === 'ingreso' ? 'success.main' : 'error.main'}>
                Editar {tipoEdit === 'ingreso' ? 'Ingreso' : 'Gasto'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Modifique los detalles de la transacción</Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setOpenEditDialog(false)}
            sx={{ position: 'absolute', right: 16, top: 16, bgcolor: alpha(theme.palette.background.paper, 0.5), '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.8) } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Tipo */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tipo de Transacción</Typography>
            <ToggleButtonGroup
              value={tipoEdit} exclusive fullWidth
              onChange={(e, v) => { if (v) { setTipoEdit(v); setCatEdit(''); } }}
              aria-label="tipo de transacción"
              sx={{
                '& .MuiToggleButtonGroup-grouped': {
                  border: 0, borderRadius: '16px !important', mx: 0.5, py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: tipoEdit === 'ingreso' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                    color: tipoEdit === 'ingreso' ? theme.palette.success.main : theme.palette.error.main,
                    '&:hover': {
                      bgcolor: tipoEdit === 'ingreso' ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2),
                    }
                  }
                }
              }}
            >
              <ToggleButton value="ingreso" sx={{ borderRadius: '16px', flexDirection: 'column', gap: 1, height: '80px' }}>
                <TrendingUpIcon color={tipoEdit === 'ingreso' ? 'success' : 'inherit'} />
                <Typography variant="body2">Ingreso</Typography>
              </ToggleButton>
              <ToggleButton value="gasto" sx={{ borderRadius: '16px', flexDirection: 'column', gap: 1, height: '80px' }}>
                <TrendingDownIcon color={tipoEdit === 'gasto' ? 'error' : 'inherit'} />
                <Typography variant="body2">Gasto</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="cat-edit-label">Categoría</InputLabel>
                <Select
                  labelId="cat-edit-label" label="Categoría" value={catEdit} onChange={(e) => setCatEdit(e.target.value)}
                  sx={{
                    borderRadius: '16px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: tipoEdit === 'ingreso' ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.error.main, 0.3),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: tipoEdit === 'ingreso' ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.error.main, 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: tipoEdit === 'ingreso' ? theme.palette.success.main : theme.palette.error.main,
                    }
                  }}
                  startAdornment={<InputAdornment position="start"><CategoryIcon color="action" /></InputAdornment>}
                >
                  {(tipoEdit === 'ingreso' ? categoriasIngresos : categoriasGastos).map(cat => (
                    <MenuItem key={cat} value={cat}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tipoEdit === 'ingreso' ? theme.palette.success.main : theme.palette.error.main }} />
                        {cat}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth label="Descripción" value={descEdit} onChange={(e) => setDescEdit(e.target.value)}
                multiline rows={3} placeholder="Añade detalles sobre esta transacción..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Monto" value={montoEdit} onChange={(e) => setMontoEdit(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="Fecha" value={fechaEdit} onChange={(e) => setFechaEdit(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 3, pt: 2 }}>
          <Button
            onClick={() => { setOpenEditDialog(false); setEditingTx(null); }}
            variant="outlined" sx={{ borderRadius: '20px', px: 3, py: 1 }}
            startIcon={<CloseIcon />}
          >
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleSaveEdit}
            sx={{
              borderRadius: '20px', px: 3, py: 1,
              bgcolor: tipoEdit === 'ingreso' ? theme.palette.success.main : theme.palette.error.main,
              '&:hover': { bgcolor: tipoEdit === 'ingreso' ? theme.palette.success.dark : theme.palette.error.dark },
            }}
            startIcon={<SaveIcon />}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicFinanzasPage;
